import isEmpty from 'lodash.isempty'
import { object } from 'prop-types'
import React, { createContext, useEffect, useState } from 'react'
import { TEMPLATE_STATUS } from '../apollo/queries/SUBMIT_ANSWERS'
import { getDataSchema } from '../components/common/form/FormikHelper'
import { getSelectedAnswersByQuestionId } from '../utils/strategyBuilder'

export const QuestionStepContext = createContext({})

const QuestionStepProvider = ({ children }) => {
  const [savingProgress, setSavingProgress] = useState(false)
  const [answersUpdating, setAnswersUpdating] = useState(false)
  const [exitingStrategyBuilder, setExitingStrategyBuilder] = useState(false)
  const [userHitBack, setUserHitBack] = useState(false)
  const [backFromPersonas, setBackFromPersonas] = useState(false)
  const [valuesFromFormik, setValuesFromFormik] = useState({})
  const [questionProgressStep, setQuestionProgressStep] = useState(0)
  const [totalQuestionProgressSteps, setTotalQuestionProgressSteps] = useState(
    0
  )
  const [nextButtonClicked, setNextButtonClicked] = useState(false)
  const [isAllAnswerValid, setIsAllAnswerValid] = useState()
  const [template, setTemplate] = useState(DefaultTemplate)
  const [formikSchema, setFormikSchema] = useState()
  const [stepheaderTitle, setStepheaderTitle] = useState('')
  const [rawTemplate, setRawTemplate] = useState()
  const [deliverables, setDeliverables] = useState([])
  const [deliverablesByCategory, setDeliverablesByCategory] = useState([])
  const [percentCurrentError, setPercentCurrentError] = useState(false)
  const [percentDesiredError, setPercentDesiredError] = useState(false)

  const filterQuestionList = newTemplate => {
    const {
      allTemplateQuestions,
      allTemplateSections,
      questions,
      currentStep,
    } = newTemplate
    const newSectionList = [1]

    // question 1c of Business Mix and Target Market determines which version of question 2 the user sees
    const retirementAnswer = questions[0][0]?.answers?.find(
      answer => answer.order === 6
    )

    const showRetirement =
      retirementAnswer?.userSelectedAnswer &&
      parseInt(
        retirementAnswer?.answerValue.substring(
          0,
          retirementAnswer?.answerValue.length
        )
      ) >= 25

    const questionTwoSection = showRetirement ? 2 : 3
    newSectionList.push(questionTwoSection)

    if (currentStep < 2) {
      newSectionList.push(4)
      newSectionList.push(5)
    } else {
      // after initial filter, questions has only 4 sections. We still want to use this list rather than allTemplateQuestions
      // because the questions list updates as the user changes/adds answers without the need for a refetch
      const questionTwoIndex =
        questions.length > 4 || showRetirement
          ? questionTwoSection - 1
          : questionTwoSection - 2
      const questionTwoGroup = questions[questionTwoIndex]
      const questionTwo = questionTwoGroup[questionTwoGroup.length - 1]
      // target mix answers are last 3 for each version of question 2
      questionTwo?.answers?.forEach(answer => {
        if (answer.userSelectedAnswer) {
          if (answer.order === 1) {
            newSectionList.push(4)
          }
          if (answer.order === 2) {
            newSectionList.push(4)
            newSectionList.push(5)
          }
        }
      })
    }

    const filteredQuestions = allTemplateQuestions?.filter(questionGroup => {
      // all questions in group share same sectionId
      return newSectionList?.includes(parseInt(questionGroup[0]?.sectionId))
    })

    const filteredSections = allTemplateSections?.filter(section => {
      return newSectionList?.includes(parseInt(section.id))
    })

    return { questions: filteredQuestions, sections: filteredSections }
  }

  const groupQuestions = questions => {
    const grouped = []
    let currentGroup = []
    questions.forEach(question => {
      if (
        !currentGroup.length ||
        currentGroup[currentGroup.length - 1]?.sectionId === question.sectionId
      ) {
        currentGroup.push(question)
      } else {
        grouped.push(currentGroup)
        currentGroup = [question]
      }
    })
    if (currentGroup.length) {
      grouped.push(currentGroup)
    }
    return grouped
  }

  const filterTargetMarketSections = template => {
    const firstSection = template.sections[0]
    const [primary] = getSelectedAnswersByQuestionId({
      questionId: '106',
      sectionId: '42',
      steps: 0,
      data: rawTemplate,
    })
    const [secondary] = getSelectedAnswersByQuestionId({
      questionId: '107',
      sectionId: '42',
      steps: 0,
      data: rawTemplate,
    })
    const [tertiary] = getSelectedAnswersByQuestionId({
      questionId: '108',
      sectionId: '42',
      steps: 0,
      data: rawTemplate,
    })
    const response = [primary, secondary, tertiary].filter(Boolean)
    if (!isEmpty(response)) {
      const selectedSections = response.map(
        ({ option, sectionText }) => `${sectionText} - ${option.text}`
      )
      const selectedIds = [firstSection.id]
      for (let i = 0; i < template.sections.length; i++) {
        const section = template.sections[i]
        if (selectedSections.find(s => s.includes(section.name))) {
          selectedIds.push(section.id)
        }
      }
      template.sections = [
        ...template.sections.filter(section =>
          selectedIds.includes(section.id)
        ),
      ]
      template.questions = template.questions
        .map(group => {
          return group.filter(question => {
            return selectedIds.includes(question.sectionId)
          })
        })
        .filter(arr => arr.length)
    }

    setTemplate(template)
  }

  useEffect(() => {
    if (template.questionIsTargetMarket)
      return filterTargetMarketSections(template)
  }, [template, rawTemplate])

  const updateTemplate = newTemplate => {
    setAnswersUpdating(true)
    const updatedTemplate = {
      ...template,
      ...newTemplate,
    }

    if (
      newTemplate.questions !== undefined &&
      !Array.isArray(newTemplate.questions[0])
    ) {
      const groupedQuestions = groupQuestions(updatedTemplate.questions)
      updatedTemplate.questions = groupedQuestions
      updatedTemplate.allTemplateQuestions = groupedQuestions
    }

    if (
      updatedTemplate.sections.length &&
      !updatedTemplate.allTemplateSections.length
    ) {
      updatedTemplate.allTemplateSections = updatedTemplate.sections
    }

    if (updatedTemplate.id === '2') {
      const filtered = filterQuestionList(updatedTemplate)
      updatedTemplate.questions = filtered.questions
      updatedTemplate.sections = filtered.sections
    }

    const currentQuestionGroup =
      updatedTemplate.questions[updatedTemplate.currentStep]
    if (currentQuestionGroup) {
      updatedTemplate.questionIsPercentGroupSection =
        currentQuestionGroup[0]?.answers[0]?.groupId &&
        currentQuestionGroup[0]?.answers[0]?.answerType === 'PercentInput'
      // currently the only marketing role question is the first question of Resource Plan
      // this logic will need to be updated if we add more marketing role questons
      updatedTemplate.questionIsMarketingRoleSection =
        currentQuestionGroup[0]?.templateId === '5' &&
        currentQuestionGroup[0]?.sectionId === '14'
      updatedTemplate.questionIsTargetMarket =
        currentQuestionGroup[0]?.templateId === '9'
      updatedTemplate.questionIsMarketingResource =
        currentQuestionGroup[0]?.templateId === '11'
      updatedTemplate.questionIsMarketingGoals =
        currentQuestionGroup[0]?.templateId === '12'
      updatedTemplate.questionIsBusinessMix =
        currentQuestionGroup[0]?.templateId === '8'
      updatedTemplate.questionIsBrandPositioning =
        currentQuestionGroup[0]?.templateId === '10'

      if (newTemplate.firstPercentGroupComplete === undefined) {
        if (updatedTemplate.questionIsPercentGroupSection) {
          if (updatedTemplate.templateStatus === TEMPLATE_STATUS.NOT_STARTED) {
            updatedTemplate.firstPercentGroupComplete = false
          } else {
            if (template.questionIsPercentGroupSection) {
              updatedTemplate.firstPercentGroupComplete =
                !template.questionIsPercentGroupSection ||
                template.currentStep === updatedTemplate.currentStep
            } else {
              updatedTemplate.firstPercentGroupComplete = userHitBack
                ? !template.questionIsPercentGroupSection
                : !template.questionIsPercentGroupSection ||
                  template.currentStep === updatedTemplate.currentStep
            }
          }
        } else {
          updatedTemplate.firstPercentGroupComplete = false
        }
      }
    }

    updatedTemplate.isSummaryPage =
      updatedTemplate.questions.length === updatedTemplate.currentStep

    setTemplate(updatedTemplate)
    setAnswersUpdating(false)
  }

  const validateAndUpdateTemplate = ({ data, currentStep = false }) => {
    if (data && data.template) {
      const newTemplate = {
        id: data.template.id,
        title: data.template.name,
        questions: data.template.questions,
        sections: data.template.sections,
        templateStatus: data.template.templateStatus,
        hasDeliverable: data.template.hasDeliverable,
      }

      setQuestionProgressStep(data.template.currentSubstepNumber || 0)
      setTotalQuestionProgressSteps(
        data.template.currentSubstepTotalNumber || 0
      )

      newTemplate.currentStep = currentStep
        ? currentStep
        : data.template.currentQuestionNumber - 1

      // previousPath is a custom variable we set when coming from the Strategy Builder overview page.
      // once inside strategy builder, we need to reset the variable so that we don't send the user back to the first step unnecessarily
      if (location?.previousPath?.pathname?.length) {
        location.previousPath.pathname = null
      }
      updateTemplate(newTemplate)
    }
  }

  const generateFormikSchema = ({ questions, type }) => {
    if (!isEmpty(questions)) {
      setFormikSchema(
        getDataSchema({
          questions,
          type,
          values: valuesFromFormik,
        })
      )
    }
  }

  const getHeaderTitle = name => {
    switch (name?.toLowerCase()) {
      case 'relationship nurturing':
        return 'Relationship Nurturing Scope'
      default:
        return template.title
    }
  }

  useEffect(() => {
    setStepheaderTitle(
      getHeaderTitle(
        template?.allTemplateSections?.[template?.currentStep]?.name
      )
    )
  }, [template, questionProgressStep])

  return (
    <QuestionStepContext.Provider
      value={{
        deliverables,
        setDeliverables,
        deliverablesByCategory,
        setDeliverablesByCategory,
        questionProgressStep,
        setQuestionProgressStep,
        totalQuestionProgressSteps,
        setTotalQuestionProgressSteps,
        answersUpdating,
        setAnswersUpdating,
        backFromPersonas,
        setBackFromPersonas,
        savingProgress,
        setSavingProgress,
        template,
        setTemplate,
        updateTemplate,
        validateAndUpdateTemplate,
        userHitBack,
        setUserHitBack,
        valuesFromFormik,
        setValuesFromFormik,
        exitingStrategyBuilder,
        setExitingStrategyBuilder,
        generateFormikSchema,
        formikSchema,
        stepheaderTitle,
        setStepheaderTitle,
        rawTemplate,
        setRawTemplate,
        nextButtonClicked,
        setNextButtonClicked,
        isAllAnswerValid,
        setIsAllAnswerValid,
        percentCurrentError,
        setPercentCurrentError,
        percentDesiredError,
        setPercentDesiredError,
      }}
    >
      {children}
    </QuestionStepContext.Provider>
  )
}

QuestionStepProvider.propTypes = {
  children: object.isRequired,
}

export default QuestionStepProvider

export const DefaultTemplate = {
  id: null,
  title: '',
  currentStep: null,
  questionProgressStep: null,
  totalProgressSteps: null,
  sections: [],
  questions: [],
  allTemplateQuestions: [],
  allTemplateSections: [],
  questionIsPercentGroupSection: false,
  questionIsMarketingRoleSection: false,
  questionIsTargetMarket: false,
  questionIsMarketingResource: false,
  firstPercentGroupComplete: false,
  templateStatus: TEMPLATE_STATUS.NOT_STARTED,
}
